import { observer } from 'mobx-react-lite';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CarListingsIcon,
  CarRequestsIcon,
  ContentReportsIcon,
  LeadsIcon,
  ReviewsIcon,
  SpecialistsIcon,
} from './icons';
import { useQueueCounts } from './QueueCountsProvider';
import { SidebarSectionHeader } from './Sidebar.styles';
import { SidebarItem } from './SidebarItem';
import { useHasPermission } from '../../auth';
import { Routes } from '../../routes/routes';

/**
 * «Площадка» sidebar section (AUT-542). Each row shows only for the
 * permission the server checks on its endpoints: «Специалисты» and
 * «Объявления» — `verify_inspectors`; «Отзывы» (AUT-542 PR2), «Жалобы» and
 * «Заявки» — `moderate_reports`. With neither, the section header is gone too.
 *
 * Only «Жалобы» carries a count (`contentReportsOpen`, open complaints);
 * zero hides it (`SidebarItem`).
 */
const SidebarMarketplaceInner = (): ReactElement | null => {
  const { t } = useTranslation();
  const counts = useQueueCounts();
  const canVerify = useHasPermission('verify_inspectors');
  const canModerate = useHasPermission('moderate_reports');
  const canSeeLeads = useHasPermission('view_pii');

  if (!canVerify && !canModerate && !canSeeLeads) return null;

  return (
    <>
      <SidebarSectionHeader>{t('nav.sections.marketplace')}</SidebarSectionHeader>
      {canVerify ? (
        <>
          <SidebarItem
            to={Routes.App.Specialists}
            icon={<SpecialistsIcon />}
            label={t('nav.specialists')}
          />
          <SidebarItem
            to={Routes.App.CarListings}
            icon={<CarListingsIcon />}
            label={t('nav.carListings')}
          />
        </>
      ) : null}
      {canModerate ? (
        <>
          <SidebarItem to={Routes.App.Reviews} icon={<ReviewsIcon />} label={t('nav.reviews')} />
          <SidebarItem
            to={Routes.App.ContentReports}
            icon={<ContentReportsIcon />}
            label={t('nav.contentReports')}
            badgeCount={counts.contentReports}
          />
          <SidebarItem
            to={Routes.App.CarRequests}
            icon={<CarRequestsIcon />}
            label={t('nav.carRequests')}
          />
        </>
      ) : null}
      {canSeeLeads ? (
        <SidebarItem to={Routes.App.Leads} icon={<LeadsIcon />} label={t('nav.leads')} />
      ) : null}
    </>
  );
};

export const SidebarMarketplace = observer(SidebarMarketplaceInner);
